import React, { useEffect } from 'react';
import './EnhancedJobDetailsModal.css';

// Status text mapping - matching Applications.js
const getStatusDisplayText = (status) => {
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
        case 'assigned':
            return 'Applied';
        case 'approved':
            return 'Shortlisted';
        case 'selected':
            return 'Selected';
        case 'rejected':
            return 'Rejected';
        case 'scheduled':
        case 'interview scheduled':
            return 'Interview Scheduled';
        case 'offered':
            return 'Offered';
        case 'in process':
            return 'In Process';
        case 'no show':
            return 'No Show';
        case 'not joined':
            return 'Not Joined';
        case 'withdrawn':
        case 'candidate withdrew':
            return 'Withdrawn';
        case 'completed 1 month':
            return 'Completed';
        case 'hired':
            return 'Hired';
        case 'position closed':
            return 'Position Closed';
        default:
            return status.charAt(0).toUpperCase() + status.slice(1);
    }
};

// Safely parse bullet lists from a string without splitting on intra-word hyphens
// Mirrors the logic used in JobDetailsModal.parseDescriptionToList
const parseDescriptionToList = (desc) => {
    if (!desc || typeof desc !== 'string') return [];
    // Split on common bullet separators, newlines, explicit start-of-line hyphen bullets, numbers, asterisks, etc.
    // IMPORTANT: do NOT split on a bare hyphen as it breaks words like "problem-solving".
    // Keep only the start-of-line hyphen bullet pattern (^-\s+).
    const parts = desc
        .split(/\r?\n|•|\u2022|\u25CF|\u25E6|\u2043|\*|^-\s+|\d+\.|\d+\)|\u2023|\u204C|\u204D/gm)
        .map(p => p.trim())
        .filter(p => p && p.length > 1);
    if (parts.length) return parts;
    // Fallback: split by sentences if no bullet-like separators are found
    return desc.split(/\.(?:\s+|$)/).map(s => s.trim()).filter(Boolean);
};

// Extract custom sections from job text (headers wrapped in asterisks like *free service*)
const extractCustomSections = (jobText) => {
    if (!jobText || typeof jobText !== 'string') return [];
    
    const customSections = [];
    // Match asterisk-wrapped text with optional colon: *text* or *text*:
    const customHeaderRegex = /\*([^*]+)\*\s*:?/g;
    let match;
    const matches = [];
    
    // Find all custom headers
    while ((match = customHeaderRegex.exec(jobText)) !== null) {
        // Remove any trailing colon from the header text
        const headerText = match[1].trim().replace(/:+$/, '');
        matches.push({
            header: headerText,
            idx: match.index,
            endIdx: match.index + match[0].length // This includes the colon if present
        });
    }
    
    // Extract content for each custom header
    matches.forEach((customHeader, index) => {
        const nextHeaderIdx = index < matches.length - 1 ? matches[index + 1].idx : jobText.length;
        const content = jobText.slice(customHeader.endIdx, nextHeaderIdx).trim();
        
        if (content) {
            customSections.push({
                header: customHeader.header,
                content: parseDescriptionToList(content)
            });
        }
    });
    
    return customSections;
};

const EnhancedJobDetailsModal = ({ isOpen, onClose, application }) => {
    
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return `₹${Number(amount).toLocaleString()}`;
    };

    // Check if salary breakdown is available
    const hasSalaryBreakdown = application?.salaryBreakdown !== null && application?.salaryBreakdown !== undefined;

    if (!isOpen || !application) return null;

    return (
        <div className="ejd-overlay" onClick={onClose}>
            <div className="ejd-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ejd-header">
                    <button className="ejd-close-btn" type="button" aria-label="Close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                    <div className="ejd-header-content">
                        <h2 className="ejd-title">{application.jobName || application.title || 'Job Title'}</h2>
                        <div className="ejd-location">
                            <i className="fas fa-map-marker-alt"></i>
                            <span>{application.location || application.cityName || 'Location not specified'}</span>
                        </div>
                    </div>
                </div>

                <div className="ejd-content" style={{ paddingBottom: '2rem' }}>
                    {/* Application Summary with Heading and Rows */}
                    <div className="ejd-application-summary-section">
                        <div className="ejd-section-header-fixed">
                            <i className="fas fa-file-alt"></i>
                            <h3>Application Summary</h3>
                        </div>
                        <div className="ejd-summary-single-row">
                            {application.appliedOn && (
                                <div className="ejd-summary-item">
                                    <span className="ejd-summary-label">Applied On</span>
                                    <span className="ejd-summary-value">{new Date(application.appliedOn).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })}</span>
                                </div>
                            )}
                            {application.candidateStatus && (
                                <div className="ejd-summary-item">
                                    <span className="ejd-summary-label">Candidate Status</span>
                                    <span className={`ejd-summary-value ${
                                        application.candidateStatus.toLowerCase().includes('rejected') || 
                                        application.candidateStatus.toLowerCase().includes('closed') ||
                                        application.candidateStatus.toLowerCase().includes('removed')
                                        ? 'ejd-status-rejected' : 'ejd-status-text'
                                    }`}>{getStatusDisplayText(application.candidateStatus)}</span>
                                </div>
                            )}
                            {(application.minSalaryLimit || application.maxSalaryLimit) && (
                                <div className="ejd-summary-item" style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span className="ejd-summary-label">Expected Monthly Salary</span>
                                        {hasSalaryBreakdown && (
                                            <div className="salary-info-tooltip-container">
                                                <i className="fas fa-info-circle" style={{ color: '#6366f1', fontSize: '14px', cursor: 'pointer' }}></i>
                                                <div className="salary-breakdown-tooltip">
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                                                            <span style={{ fontSize: '11px', color: '#374151', fontWeight: '500' }}>Fixed</span>
                                                            <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#111827' }}>
                                                                {application.isSingleOfferCtc === true ? (
                                                                    formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.fixedCtc / 12))
                                                                ) : (
                                                                    `${formatCurrency(Math.round(application.salaryBreakdown.minimumSalary?.fixedCtc / 12))} - ${formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.fixedCtc / 12))}`
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#6b7280' }}></div>
                                                            <span style={{ fontSize: '11px', color: '#374151', fontWeight: '500' }}>Net Take Home</span>
                                                            <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#111827' }}>
                                                                {application.isSingleOfferCtc === true ? (
                                                                    formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.netTakeHome / 12))
                                                                ) : (
                                                                    `${formatCurrency(Math.round(application.salaryBreakdown.minimumSalary?.netTakeHome / 12))} - ${formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.netTakeHome / 12))}`
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f59e0b' }}></div>
                                                            <span style={{ fontSize: '11px', color: '#374151', fontWeight: '500' }}>Variable</span>
                                                            <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#111827' }}>
                                                                {application.isSingleOfferCtc === true ? (
                                                                    formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.variableCtc / 12))
                                                                ) : (
                                                                    `${formatCurrency(Math.round(application.salaryBreakdown.minimumSalary?.variableCtc / 12))} - ${formatCurrency(Math.round(application.salaryBreakdown.maximumSalary?.variableCtc / 12))}`
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <span className="ejd-summary-value">
                                        {(() => {
                                            const minSalary = application.minSalaryLimit;
                                            const maxSalary = application.maxSalaryLimit;
                                            const isSingleOffer = application.isSingleOfferCtc === true;
                                            
                                            if (isSingleOffer && maxSalary) {
                                                return `Approx ₹${Number(maxSalary).toLocaleString()}`;
                                            } else if (minSalary && maxSalary && minSalary !== maxSalary) {
                                                return `₹${Number(minSalary).toLocaleString()} - ₹${Number(maxSalary).toLocaleString()}`;
                                            } else if (maxSalary) {
                                                return `₹${Number(maxSalary).toLocaleString()}`;
                                            } else if (minSalary) {
                                                return `₹${Number(minSalary).toLocaleString()}`;
                                            }
                                            return 'Not Disclosed';
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Job Description Section - using jobDetails parameter */}
                    <div className="ejd-section">
                        <div className="ejd-section-header-fixed">
                            <i className="fas fa-file-text"></i>
                            <h3>Job Description</h3>
                        </div>
                        <div className="ejd-section-content-fixed">
                            {(() => {
                                const jobText = application.jobDetails || application.description || '';
                                
                                if (jobText) {
                                    // Remove Responsibilities, Requirements, and custom sections from job description
                                    // Anchor to line start to avoid matching "requirements" mid-sentence
                                    let cleanJobDescription = jobText
                                        .replace(/(?:^|\n)\s*Responsibilities:\s*[\s\S]*?(?=(?:^|\n)\s*Requirements:|$)/im, '')
                                        .replace(/(?:^|\n)\s*Requirements:\s*[\s\S]*$/im, '')
                                        .replace(/\*[^*]+\*\s*:?[\s\S]*?(?=\*[^*]+\*|$)/g, '')
                                        .trim();
                                    
                                    return <p>{cleanJobDescription || 'No job description available'}</p>;
                                } else {
                                    return <p>{`jobDetails: ${JSON.stringify(application.jobDetails)}`}</p>;
                                }
                            })()}
                        </div>
                    </div>

                    {/* Responsibilities Section - only show if Responsibilities: is found */}
                    {(() => {
                        const jobText = application.jobDetails || application.description || '';
                        // Anchor to line start to avoid matching mid-sentence
                        const responsibilitiesMatch = jobText.match(/(?:^|\n)\s*Responsibilities:\s*([\s\S]*?)(?=(?:^|\n)\s*Requirements:|$)/im);
                        
                        if (responsibilitiesMatch && responsibilitiesMatch[1]?.trim()) {
                            const responsibilityText = responsibilitiesMatch[1].trim();
                            const items = parseDescriptionToList(responsibilityText);
                            
                            return (
                                <div className="ejd-section">
                                    <div className="ejd-section-header-fixed">
                                        <i className="fas fa-tasks"></i>
                                        <h3>Responsibilities</h3>
                                    </div>
                                    <div className="ejd-section-content-fixed">
                                        {items.length > 0 ? (
                                            <ul className="ejd-list-clean">
                                                {items.map((item, index) => (
                                                    <li key={index}>{item.trim()}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>{responsibilityText}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Requirements Section - only show if Requirements: is found */}
                    {(() => {
                        const jobText = application.jobDetails || application.description || '';
                        // Anchor to line start to avoid matching mid-sentence
                        const requirementsMatch = jobText.match(/(?:^|\n)\s*Requirements:\s*([\s\S]*?)$/im);
                        
                        if (requirementsMatch && requirementsMatch[1]?.trim()) {
                            const requirementText = requirementsMatch[1].trim();
                            const items = parseDescriptionToList(requirementText);
                            
                            return (
                                <div className="ejd-section">
                                    <div className="ejd-section-header-fixed">
                                        <i className="fas fa-check-circle"></i>
                                        <h3>Requirements</h3>
                                    </div>
                                    <div className="ejd-section-content-fixed">
                                        {items.length > 0 ? (
                                            <ul className="ejd-list-clean">
                                                {items.map((item, index) => (
                                                    <li key={index}>{item.trim()}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>{requirementText}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Skills Required Section - using skills parameter */}
                    <div className="ejd-section">
                        <div className="ejd-section-header-fixed">
                            <i className="fas fa-lightbulb"></i>
                            <h3>Skills Required</h3>
                        </div>
                        <div className="ejd-section-content-fixed">
                            <div className="ejd-skills-container-clean">
                                {(() => {
                                    let skillsArray = [];
                                    
                                    // Handle different skills data formats
                                    if (Array.isArray(application.skills)) {
                                        skillsArray = application.skills;
                                    } else if (typeof application.skills === 'string' && application.skills.trim()) {
                                        // Split comma-separated string into array
                                        skillsArray = application.skills.split(',').map(skill => skill.trim()).filter(skill => skill);
                                    }
                                    
                                    return skillsArray.length > 0 ? (
                                        skillsArray.map((skill, index) => (
                                            <span key={index} className="ejd-skill-tag">
                                                {skill}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="ejd-skill-tag">
                                            {application.skills ? `skills: ${JSON.stringify(application.skills)}` : 'No skills specified'}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Custom Sections - from asterisk-wrapped headers like *free service* */}
                    {(() => {
                        const jobText = application.jobDetails || application.description || '';
                        const customSections = extractCustomSections(jobText);
                        
                        return customSections.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="ejd-section">
                                <div className="ejd-section-header-fixed">
                                    <i className="fas fa-star"></i>
                                    <h3>{section.header}</h3>
                                </div>
                                <div className="ejd-section-content-fixed">
                                    {section.content.length > 0 ? (
                                        <ul className="ejd-list-clean">
                                            {section.content.map((item, itemIndex) => (
                                                <li key={itemIndex}>{item.trim()}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p>No content available</p>
                                    )}
                                </div>
                            </div>
                        ));
                    })()}

                </div>


            </div>
        </div>
    );
};

export default EnhancedJobDetailsModal;
